import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  DollarSign,
  Users,
  BookOpen,
  Star,
  ArrowUp,
  Plus,
  Settings,
  Download,
  Calendar,
  Sparkles,
  CalendarCheck,
  ChevronRight,
  FileText,
  Link as LinkIcon,
  CreditCard,
  Loader2,
  BarChart3,
  UserCheck,
  ClipboardList,
  LayoutGrid
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/stats`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Revenue',
      value: `$${(stats?.total_revenue || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'emerald'
    },
    {
      title: 'Total Students',
      value: (stats?.total_students || 0).toLocaleString(),
      icon: Users,
      color: 'teal'
    },
    {
      title: 'Active Courses',
      value: stats?.active_courses || 0,
      icon: BookOpen,
      color: 'cyan'
    },
    {
      title: 'Avg. Rating',
      value: stats?.average_rating || 'N/A',
      icon: Star,
      color: 'yellow'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950 pt-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Admin Dashboard</h1>
            <p className="text-gray-400">Overview of your platform's performance</p>
          </div>
          <div className="flex items-center gap-3 mt-4 md:mt-0">
            <Button className="bg-amber-500 hover:bg-amber-600 text-black font-medium" onClick={() => navigate('/admin/courses/new')}>
              <Plus className="w-4 h-4 mr-2" />
              New Course
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Manage Courses', desc: 'Edit, create, or delete courses', icon: BookOpen, color: 'emerald', path: '/admin/courses' },
            { label: 'Manage Blog', desc: 'Write & publish articles', icon: FileText, color: 'purple', path: '/admin/blog' },
            { label: 'Manage Consultations', desc: 'Edit prices & descriptions', icon: Sparkles, color: 'amber', path: '/admin/consultations' },
            { label: 'View Bookings', desc: 'Manage client consultations', icon: CalendarCheck, color: 'teal', path: '/admin/bookings' },
            { label: 'Manage Links', desc: 'Update homepage links', icon: LinkIcon, color: 'rose', path: '/admin/links' },
            { label: 'Manage Events', desc: 'Update homepage events', icon: Calendar, color: 'sky', path: '/admin/events' },
            { label: 'Page Sections', desc: 'Edit images & CTA buttons', icon: LayoutGrid, color: 'amber', path: '/admin/sections' },
            { label: 'Consultation Fees', desc: 'Manage services & pricing', icon: CreditCard, color: 'cyan', path: '/admin/services' },
            { label: 'Schedule & Activities', desc: 'Monthly & yearly calendar', icon: Calendar, color: 'violet', path: '/admin/schedule' },
            { label: 'Student Control Panel', desc: 'Monitor & control access', icon: Users, color: 'blue', path: '/admin/students' },
          ].map((action) => (
            <button
              key={action.path}
              onClick={() => navigate(action.path)}
              data-testid={`admin-nav-${action.path.split('/').pop()}`}
              className="flex items-center justify-between p-4 bg-gray-900/50 border border-gray-800 rounded-lg hover:border-gray-600 hover:bg-gray-900 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-${action.color}-500/10 rounded-lg flex items-center justify-center`}>
                  <action.icon className={`w-5 h-5 text-${action.color}-400`} />
                </div>
                <div className="text-left">
                  <p className="text-white font-medium">{action.label}</p>
                  <p className="text-gray-500 text-sm">{action.desc}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" />
            </button>
          ))}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <Card key={index} className="bg-gray-900/50 border-gray-800" data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 bg-${stat.color}-500/10 rounded-xl flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 text-${stat.color}-400`} />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-gray-500 text-sm">{stat.title}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xl font-bold text-white">{stats?.total_bookings || 0}</p>
                <p className="text-gray-500 text-xs">Total Bookings</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-xl font-bold text-white">{stats?.pending_bookings || 0}</p>
                <p className="text-gray-500 text-xs">Pending Bookings</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xl font-bold text-white">{stats?.total_enrollments || 0}</p>
                <p className="text-gray-500 text-xs">Total Enrollments</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xl font-bold text-white">{stats?.total_blog_posts || 0}</p>
                <p className="text-gray-500 text-xs">Blog Posts</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tables Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Enrollments */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Recent Enrollments</h3>
              </div>
              <div className="space-y-4">
                {(stats?.recent_enrollments || []).length > 0 ? (
                  stats.recent_enrollments.map((enrollment, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg" data-testid={`recent-enrollment-${idx}`}>
                      <div>
                        <p className="text-white font-medium">{enrollment.student}</p>
                        <p className="text-gray-500 text-sm">{enrollment.course}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-amber-400 font-medium">${enrollment.amount}</p>
                        <p className="text-gray-500 text-sm">{formatTimeAgo(enrollment.date)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No enrollments yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Courses */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Top Courses</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/courses')} className="text-amber-400 hover:text-amber-300">
                  View all
                </Button>
              </div>
              <div className="space-y-4">
                {(stats?.top_courses || []).length > 0 ? (
                  stats.top_courses.map((course, index) => (
                    <div key={course.id} className="flex items-center gap-4 p-3 bg-gray-800/30 rounded-lg" data-testid={`top-course-${index}`}>
                      <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                        {index + 1}
                      </div>
                      <div className="w-16 h-10 rounded overflow-hidden shrink-0">
                        <img
                          src={course.thumbnail}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{course.title}</p>
                        <p className="text-gray-500 text-sm">{course.total_students || 0} students</p>
                      </div>
                      <div className="text-right">
                        <p className="text-amber-400 font-medium">${course.price}</p>
                        <div className="flex items-center gap-1 justify-end">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-gray-500 text-sm">{course.rating}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No courses yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Bookings */}
        {(stats?.recent_bookings || []).length > 0 && (
          <Card className="bg-gray-900/50 border-gray-800 mt-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Recent Bookings</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/bookings')} className="text-amber-400 hover:text-amber-300">
                  View all
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.recent_bookings.map((booking, idx) => (
                  <div key={idx} className="p-4 bg-gray-800/30 rounded-lg" data-testid={`recent-booking-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">{booking.client_name}</p>
                      <Badge className={`${booking.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                        {booking.status}
                      </Badge>
                    </div>
                    <p className="text-gray-400 text-sm">{booking.consultation_name}</p>
                    <p className="text-gray-500 text-xs mt-1">{booking.date} at {booking.time_slot}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
